// ============================================================================
// New Account SOP data hook — single source of truth for the SOP module.
//
// Same TanStack-Query-shaped surface (data, isLoading, error, refetch, mutate)
// used by HR & Hiring (modules/hr-hiring/hooks/use-hiring-data.ts). Plain
// useState/useEffect under the hood — TanStack Query isn't installed.
//
// RLS notes (verified 2026-05-05):
//   * superuser     → SELECT all 74 rows (Ben confirmed)
//   * account_manager → am_read_all + am_update_sop
//   * finance       → finance_read_all + finance_update_sop
//   * hr            → hr_read_all + hr_update_sop
//   * sales         → sales_read + sales_insert + sales_update
// All authenticated roles in the current UserRole union can read; writes are
// allowed for everyone except finance-read-only (which we don't model).
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type { Json } from '../../../types/database.types';
import { toNumOrNull, toIntOrNull } from '../../../shared/coerce';
import type { NewAccountSop } from '../../../shared/types';
import { errorMessage } from '../../../shared/errors';
import { refreshReaders, registerReader } from '../../../shared/save-path';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Editable subset for inline edit. Phase 2 (2026-05-28) expanded this from
// the original 4 fields to include the detail-drawer-tab editable fields:
// status / stage / priority / notes plus the Info / Pricing / Contractor /
// Checklist tab fields. The detail drawer batches a single field per save
// so the partial shape stays narrow.
export type SopEditableField =
  | 'stage'
  | 'priority'
  | 'status'
  | 'notes'
  // ⛔ 'account_name' was here and is deliberately gone (2026-08-26, TCSD-0118).
  // The detail drawer's editable "Account name" box wrote this text column and did
  // NOT change the account the card points at — it looked like renaming the
  // customer and never was. Nothing writes it now except the intake, which is the
  // one case it exists for: a customer typed before an account row exists for them.
  // A field with no writer that still sits in an editable union is how the next
  // person re-adds the box (TCSD-0092: name the caller).
  | 'facility_type'
  | 'market_region'
  // ⛔ 'market_color' was here and is deliberately gone (2026-09-10). The app
  // used to send it from an eleven-entry hex map in its own constants, which is
  // how one live card came to store Houston's dot on an Austin market: the
  // market moved and the colour did not follow. The colour now lives on the
  // market's own row in public.markets and a BEFORE trigger keeps the card's
  // copy in step. Leaving it in this union is how the next person re-adds the
  // second writer (TCSD-0092: name the caller).
  | 'start_date'
  | 'final_pricing'
  | 'management_fee_pct'
  // AFB-0081. This was captured at intake and then editable NOWHERE: absent
  // from this union, so nothing in the app could write it, and unrendered in
  // sop-detail, so nothing could show it either. 31 of 36 cards carry NULL and
  // 3 of those are in the live pipeline — which the Phase 6b gate would have
  // turned into a dead end rather than a prompt. Whoever is being asked for an
  // answer has to be able to give one.
  | 'tcs_provides_usables'
  | 'target_margin_pct'
  | 'total_sqft'
  | 'sterile_sqft'
  | 'non_sterile_sqft'
  // AFB-0032 — OR / PR room counts for surgery centers. Captured on the SOP
  // create modal already; this makes them editable from the SOP detail Info
  // tab too (and keeps them on the new_account_sop row).
  | 'or_count'
  | 'pr_count'
  | 'is_draft'
  | 'draft_last_saved_at'
  | 'bdd_eid'
  | 'am_eid'
  | 'insurance_doc_link'
  | 'employment_agreement_link'
  | 'building_service_agreement_link'
  | 'signed_proposal_url'
  | 'attachments'
  | 'payment_terms'
  | 'payment_frequency'
  | 'service_days_per_week'
  | 'billing_contact_name'
  | 'billing_contact_email'
  | 'billing_contact_phone'
  | 'billing_contact_address'
  | 'client_address_street'
  | 'client_address_city'
  | 'client_address_state'
  | 'client_address_zip'
  // The building's one address line. Editable from the card only once the
  // building exists; before that the four boxes above are the intake.
  | 'facility_address'
  // ⛔ WHO CLEANS THIS BUILDING AND WHAT THEY ARE PAID IS NOT LISTED HERE.
  // is_new_subcontractor / service_provider_id / service_lines / contractor_name
  // / contractor_phone / contractor_email / sub_payment / no_miss_incentive were
  // columns on the card until 2026-09-18 and are now columns on the building's
  // service line. The card's window still SERVES all eight — it reads them from
  // the building — so nothing that displays them changed. What changed is that
  // there is nowhere on the card to write them, so they cannot be patched.
  // Write them with save_service_line(); nas_card_patch refuses them by name
  // and says so.
  | 'checklist'
  | 'startup_checklist'
  | 'cancelled_date'
  | 'cancelled_reason';
export type SopPatch = Partial<Pick<NewAccountSop, SopEditableField>>;

export interface SopDataResult {
  data: NewAccountSop[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
  // Optimistic patch. Merges into the local cache immediately, PATCHes the
  // row, rolls back on failure. Throws on error so the caller can surface it.
  patchRow: (sopId: string, patch: SopPatch) => Promise<void>;
  // #6 — delete a card via the delete_new_account_sop RPC (deletion_requests
  // workflow; owner-or-admin enforced server-side). Optimistically removes the
  // row and rolls back on failure. Throws so the caller can surface it.
  deleteRow: (sopId: string, reason: string) => Promise<void>;
  /**
   * Set when the save door REFUSED one or more fields of a patch — the rate
   * (which is the sum of its service lines and has its own door), the customer
   * (moving a building between customers is an approval), or an account manager
   * on a finalized building (leadership only).
   *
   * ⛔ Deliberately a per-field refusal and not a throw. One field the person
   * may not change must not discard the four they may — and the refused field
   * is rolled back on screen, so nobody is left reading their own typing and
   * believing it is stored. The message is the database's own sentence, never
   * a second copy of the rule written here.
   */
  refusalNotice: string | null;
  clearRefusalNotice: () => void;
}


// ---------------------------------------------------------------------------
// Read hook — full row select. The list is small (74 today, probably <500
// long-term) and we render a Kanban + a list view that both want every
// surfaced field, so a one-shot select is simpler than per-view projections.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Lifecycle RPCs (Option D PR-4a). Both are SECURITY DEFINER + role-gated +
// REVOKEd from anon server-side; the client just calls them and refetches.
// ---------------------------------------------------------------------------

/** Confirm an account actually went live (D2). The actual start date drives
 *  billing / annual kicker / proration — pass the REAL date, not "today". */
export async function confirmAccountStarted(
  facilityId: string,
  actualStartDate: string | null,
  /** The services that are NOT starting, each with its reason (the exception).
   *  Everything else on the building starts with it. Built by
   *  StartingServicesFields — never assembled by a caller. */
  notStarting: ReadonlyArray<{ assignment_id: string; reason: string }> = [],
): Promise<void> {
  const { error } = await supabase.rpc('confirm_account_started', {
    p_facility_id: facilityId,
    p_actual_start_date: actualStartDate,
    p_not_starting: notStarting as unknown as Json,
  });
  if (error) throw error;
}

/** Correct an account's start date anytime — actual (contract_start, the
 *  billing anchor) and/or planned (the countdown). Works on already-Active
 *  accounts that got stuck without a real start date during the transition. */
export async function updateAccountStartDate(
  facilityId: string,
  opts: {
    actual?: string | null;
    planned?: string | null;
    /** Why it slipped. Required by the database on a building that has not
     *  opened — it is recorded, not approved, and it lands in the building's
     *  own lifecycle note. */
    reason?: string | null;
    /** ⛔ "We don't know yet" is a real answer. Clearing the date does NOT stop
     *  the daily ask: a building with no start date is named in it every day
     *  rather than skipped silently. */
    clearPlanned?: boolean;
  },
): Promise<void> {
  const { error } = await supabase.rpc('update_account_start_date', {
    p_facility_id: facilityId,
    p_actual_start_date: opts.actual ?? null,
    p_planned_start_date: opts.planned ?? null,
    p_reason: opts.reason ?? null,
    p_clear_planned: opts.clearPlanned ?? false,
  });
  if (error) throw error;
  // ⛔ DECLARING WHAT YOU WRITE WITHOUT DECLARING WHAT YOU READ IS THE SAME AS
  // DECLARING NOTHING (TCSD-0248, PLAYBOOK §15 question 2). This is an RPC, so
  // commitWrite's zero-row proof does not apply — the function raises or it
  // does not. Question 2 still does: the function writes BOTH facilities and
  // new_account_sop, and the building page reads the first while the board
  // reads the second, so both are declared here rather than at two call sites.
  refreshReaders(['facilities', 'new_account_sop']);
}

export type SopSignoffKind = 'exec_review' | 'confirmation_call';

/** Record — or take back — the executive review / confirmation call sign-off.
 *
 *  These two columns existed since the schema was extended and were set on 0 of
 *  38 rows EVER, because nothing in the app could write them: the requirement
 *  view asked for a signature and the product offered no pen. Ben, 2026-08-24,
 *  D1=C: retire the ask on Completed accounts, keep it for the in-flight ones,
 *  and build the buttons.
 *
 *  Who may sign is enforced server-side, not here — exec review is leadership
 *  only (deliberately excluding the AM who is waiting on it), the confirmation
 *  call is the account's own manager or leadership. The acting employee is
 *  stamped from the session, never passed in. */
export async function recordSopSignoff(
  sopId: string,
  kind: SopSignoffKind,
  opts?: { when?: string | null; clear?: boolean },
): Promise<string | null> {
  const { data, error } = await supabase.rpc('sop_record_signoff', {
    p_sop_id: sopId,
    p_kind: kind,
    p_when: opts?.when ?? null,
    p_clear: opts?.clear ?? false,
  });
  if (error) throw error;
  return (data as string | null) ?? null;
}

// ---------------------------------------------------------------------------
// Numeric field sanitisation.  Blank HTML inputs produce "" which Supabase
// sends to Postgres as-is — and numeric/integer columns reject it with
// `invalid input syntax for type numeric: ""`.  Before every write we coerce
// known numeric fields: "" / whitespace → null, explicit 0 → 0.
// ---------------------------------------------------------------------------

/** Decimal-precision numeric columns on `new_account_sop`. */
const NAS_NUMERIC_FIELDS: ReadonlySet<string> = new Set([
  'final_pricing',
  'management_fee_pct',
  'target_margin_pct',
  'total_sqft',
  'sterile_sqft',
  'non_sterile_sqft',
]);

/** Integer columns on `new_account_sop`. */
const NAS_INT_FIELDS: ReadonlySet<string> = new Set([
  'or_count',
  'pr_count',
  'service_days_per_week',
  'days_in_stage',
]);

/** Coerce any "" / whitespace values in known numeric columns to null. */
function sanitiseNumericPatch(patch: SopPatch): SopPatch {
  const out = { ...patch };
  for (const key of Object.keys(out) as SopEditableField[]) {
    if (NAS_NUMERIC_FIELDS.has(key)) {
      (out as Record<string, unknown>)[key] = toNumOrNull(
        (out as Record<string, unknown>)[key],
      );
    } else if (NAS_INT_FIELDS.has(key)) {
      (out as Record<string, unknown>)[key] = toIntOrNull(
        (out as Record<string, unknown>)[key],
      );
    }
  }
  return out;
}

/**
 * The card's one save door (nas_card_patch) — the footer, the Kanban drag and
 * Ask AI's new-account card all come through here. Returns the fields the
 * database REFUSED, each with its own sentence (a per-field refusal, not a
 * throw: one field the person may not change must not discard the others).
 */
export async function patchSopCard(sopId: string, rawPatch: SopPatch): Promise<Record<string, string>> {
  const patch = sanitiseNumericPatch(rawPatch);
  const { data: res, error } = await supabase.rpc('nas_card_patch', {
    p_sop_id: sopId,
    p_patch: patch as Record<string, unknown>,
  });
  if (error) throw error;
  return (res as { refused?: Record<string, string> } | null)?.refused ?? {};
}

/** One card as the board reads it (v_nas_card), or null — for a save that must
 *  check the card as it is now (Ask AI's new-account card). */
export async function fetchSopCard(sopId: string): Promise<NewAccountSop | null> {
  const { data, error } = await supabase.from('v_nas_card').select('*').eq('sop_id', sopId).maybeSingle();
  if (error) throw error;
  return (data as unknown as NewAccountSop | null) ?? null;
}

/** What deleting this card does to the building it made (Ben 2026-10-26, option C):
 *  the building goes with the card only when it has no history; otherwise it stays.
 *  Read from nas_delete_preview — the same answer delete_new_account_sop acts on. */
export interface SopDeletePreview {
  building_id: string | null;
  building_name?: string | null;
  building_goes: boolean;
  why_stays: string | null;
}
export async function previewSopDelete(sopId: string): Promise<SopDeletePreview> {
  const { data, error } = await supabase.rpc('nas_delete_preview', { p_sop_id: sopId });
  if (error) throw error;
  return data as SopDeletePreview;
}

/** The sentence the confirm shows about the building, or null when the card made none. */
export function deleteBuildingWords(p: SopDeletePreview | null): string | null {
  if (!p?.building_id) return null;
  const name = p.building_name || p.building_id;
  return p.building_goes
    ? `The building it made, ${name}, is deleted too — nothing else is recorded on it.`
    : `The building ${name} stays, because ${p.why_stays ?? 'it has history'}; only the card is removed.`;
}

/** Delete a card made in error (delete_new_account_sop: the creator or full AM
 *  authority, a reason required, a snapshot written to deletion_requests first).
 *  Its building goes too when it has no history (previewSopDelete). */
export async function deleteSopCard(sopId: string, reason: string): Promise<{ building_deleted?: boolean } | null> {
  const { data, error } = await supabase.rpc('delete_new_account_sop', { p_sop_id: sopId, p_reason: reason });
  if (error) throw error;
  // The building may be gone, or no longer point at the card: both screens re-read.
  refreshReaders(['facilities', 'new_account_sop']);
  return (data as { building_deleted?: boolean } | null) ?? null;
}

/** Archive a card (the close) or restore it to the board — nas_archive_card:
 *  finance or superuser. */
export async function archiveSopCard(sopId: string, archive: boolean): Promise<string | null> {
  const { data, error } = await supabase.rpc('nas_archive_card', { p_sop_id: sopId, p_archive: archive });
  if (error) throw error;
  return (data as string | null) ?? null;
}

/**
 * The customer a card points at, resolved through `account_id`.
 *
 * ⛔ TCSD-0118 — never re-derive an identity the database can establish.
 * `new_account_sop.account_name` is free text captured at intake and it drifts:
 * on 2026-08-26, 10 of 38 rows disagreed with the account they point at, because
 * reparenting a building moves the account and leaves the text behind.
 *
 * The text column survives for exactly one case — a card created before its
 * customer exists as an account — so it is the fallback, never the answer.
 */
export function resolveCustomers(
  rows: NewAccountSop[],
  byId: Map<string, string | null>,
): NewAccountSop[] {
  return rows.map((r) => resolveCustomer(r, byId));
}

export function resolveCustomer(r: NewAccountSop, byId: Map<string, string | null>): NewAccountSop {
  if (!r.account_id) return r;
  const real = byId.get(r.account_id);
  if (!real || real === r.account_name) return r;
  return { ...r, account_name: real };
}

export function useNewAccountSops(): SopDataResult {
  const [data, setData] = useState<NewAccountSop[]>([]);
  // account_id → the account's OWN name. Kept so a Realtime payload, which
  // carries the base row and nothing joined to it, can be resolved the same way
  // the initial load was. Without this a live UPDATE would quietly put the stale
  // text column back on screen.
  const [accountNames, setAccountNames] = useState<Map<string, string | null>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);
  // A card edit that SAVED but did not reach the building it created. Separate
  // from `error` on purpose: `error` means the read or the save failed, this
  // means the save worked and the mirror did not follow.
  const [refusalNotice, setRefusalNotice] = useState<string | null>(null);

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Keep prior values visible while we re-fetch on a manual refetch tick;
      // only flip isLoading on the initial load (lastUpdated null).
      setIsLoading((prev) => (lastUpdated === null ? true : prev));
      setError(null);
      try {
        // `select('*')` because the table is wide and the detail view wants
        // nearly all of them. Cheaper than two round-trips.
        //
        // ⛔ 2026-08-26, TCSD-0118. `new_account_sop.account_name` is free text
        // captured at intake — a COPY of an identity the database already owns,
        // sitting next to the account_id that owns it. On the day the DaVita and
        // Fresenius buildings were reparented, 10 of 38 rows drifted: both
        // Fresenius Topeka cards named the clinic as the customer instead of
        // Fresenius Medical Care, and the My Work board showed SOP-0208's
        // customer as "Davita Red Rocks" rather than DaVita, Inc.
        //
        // So the customer is resolved through the id, once, here — and every
        // reader of this hook (card, list, detail, search) gets the right answer
        // without asking. The text column is the fallback only for a card whose
        // customer has no account row yet, which is the one case it exists for.
        const [{ data: rows, error: e }, { data: accts, error: ae }, { data: lcs, error: le }] = await Promise.all([
          // ⛔ v_nas_card, NOT new_account_sop. Twelve operational fields —
          // name, address, type, market, terms, start date, fee, usables,
          // service days, rate, account manager, customer — resolve from the
          // BUILDING the card created. The card's own column shows through
          // only where the building is silent, and `intake_only_fields` names
          // exactly where that is still happening. Reading the table directly
          // is how the screen came to show a value the building disagreed with.
          supabase
            .from('v_nas_card')
            .select('*')
            .order('created_date', { ascending: false, nullsFirst: false }),
          supabase.from('accounts').select('account_id, account_name'),
          // The lifecycle (starting soon / behind / started …) is decided by the
          // database — building_lifecycle() via v_new_account_lifecycle — and
          // read here, never computed on the board (2026-09-25).
          supabase.from('v_new_account_lifecycle').select('sop_id, lifecycle'),
        ]);
        if (cancelled) return;
        if (e) throw e;
        if (ae) throw ae;
        if (le) throw le;
        const lifecycleBySop = new Map(
          (lcs ?? []).map((r) => [r.sop_id as string, (r.lifecycle as string | null) ?? null]),
        );
        const byId = new Map(
          (accts ?? []).map((a) => [a.account_id as string, a.account_name as string | null]),
        );
        setAccountNames(byId);
        setData(
          resolveCustomers((rows ?? []) as unknown as NewAccountSop[], byId).map((r) => ({
            ...r,
            lifecycle: lifecycleBySop.get(r.sop_id) ?? null,
          })),
        );
        setLastUpdated(new Date());
      } catch (err) {
        if (cancelled) return;
        console.error('[new-account-sop] load failed', err);
        setError(
          errorMessage(err, 'Failed to load SOPs.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  // Register this loader against the tables it reads, so a write anywhere in
  // the app that touches them re-reads this hook without the writer needing to
  // know this screen exists. The Realtime subscription below is the stronger
  // mechanism and stays; this is the floor under it — Realtime is per-table
  // publication and only covers the SOP table, not the accounts join that
  // resolves each card's customer.
  // ⛔ 'facilities' is in this list because the card's twelve operational fields
  // now LIVE there. A rate change or an AM reassignment made on the building's
  // own card changes what this screen shows, and a reader that does not know
  // that is a screen showing yesterday's answer.
  useEffect(
    () => registerReader(['new_account_sop', 'accounts', 'facilities'], refetch),
    [refetch],
  );

  // ---------------------------------------------------------------------
  // Supabase Realtime — INSERT/UPDATE/DELETE on public.new_account_sop
  // refresh the local cache. Cheap subscription (no filter; ~100 rows).
  // Reconnects automatically across tab visibility changes.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const channel = supabase
      .channel('public:new_account_sop:kanban')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'new_account_sop' },
        (payload) => {
          // INSERT — append the new row. UPDATE — merge in place. DELETE —
          // remove. Stage drag-and-drop already optimistically updates so
          // a remote UPDATE re-confirming the same value is a no-op.
          // ⛔ A Realtime payload is the BASE ROW — it carries the stale
          // account_name text column and nothing joined to it. Merging it raw
          // would put the wrong customer straight back on screen a moment after
          // the load resolved it. Every row that arrives here goes through the
          // same resolution the initial load used. TCSD-0118.
          setData((rows) => {
            if (payload.eventType === 'INSERT' && payload.new) {
              const newRow = resolveCustomer(payload.new as NewAccountSop, accountNames);
              if (rows.some((r) => r.sop_id === newRow.sop_id)) return rows;
              return [newRow, ...rows];
            }
            if (payload.eventType === 'UPDATE' && payload.new) {
              const upd = payload.new as NewAccountSop;
              return rows.map((r) =>
                r.sop_id === upd.sop_id ? resolveCustomer({ ...r, ...upd }, accountNames) : r,
              );
            }
            if (payload.eventType === 'DELETE' && payload.old) {
              const oldRow = payload.old as Partial<NewAccountSop>;
              return rows.filter((r) => r.sop_id !== oldRow.sop_id);
            }
            return rows;
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // accountNames is read inside the handler, so the subscription is rebuilt
    // when it arrives — otherwise the first payloads after a cold load would
    // resolve against an empty map and fall back to the stale text column.
  }, [accountNames]);

  const patchRow = useCallback(
    async (sopId: string, rawPatch: SopPatch) => {
      // Coerce blank numeric strings → null before they reach Postgres.
      const patch = sanitiseNumericPatch(rawPatch);

      // Snapshot for rollback. We capture the prior value of every field in
      // the patch so a multi-field patch rolls back cleanly. Built as a
      // Record<string, unknown> internally and re-typed at the merge site so
      // the writer can index by string key without fighting the union type.
      let snapshot: Record<string, unknown> | null = null;
      setData((rows) =>
        rows.map((r) => {
          if (r.sop_id !== sopId) return r;
          const snap: Record<string, unknown> = {};
          for (const k of Object.keys(patch) as SopEditableField[]) {
            snap[k] = r[k];
          }
          snapshot = snap;
          return { ...r, ...patch };
        }),
      );
      try {
        // ===== ONE DOOR, AND IT OPENS ONTO THE BUILDING =================
        //
        // This used to PATCH new_account_sop and then run a reconcile to copy
        // the change onto the building — two stores kept in step at a gate,
        // which is the model Ben struck out on 2026-09-10: "I want one source
        // of truth. The onboarding card is one PATH to the truth — NOT two
        // sources that sync at certain gates."
        //
        // nas_card_patch decides per field which record actually owns the
        // answer and writes THAT one. There is nothing to reconcile afterwards
        // because nothing wrote a second copy. It refuses per field rather
        // than throwing, so a field the person may not change (the rate, the
        // customer, an AM on a finalized building) does not discard the four
        // they may.
        const refused = await patchSopCard(sopId, patch);
        const refusedKeys = Object.keys(refused);
        if (refusedKeys.length > 0) {
          // Put back what did not move, so the screen stops showing the
          // person their own typing as though it were stored.
          if (snapshot) {
            const snap = snapshot as Record<string, unknown>;
            const undo: Record<string, unknown> = {};
            for (const k of refusedKeys) undo[k] = snap[k];
            setData((rows) =>
              rows.map((r) => (r.sop_id === sopId ? { ...r, ...(undo as SopPatch) } : r)),
            );
          }
          setRefusalNotice(
            refusedKeys.map((k) => `${k}: ${refused[k]}`).join(' '),
          );
        } else {
          setRefusalNotice(null);
        }
        // Operational fields now live on the building, so anything reading it
        // is stale until it refetches.
        refetch();

      } catch (err) {
        console.error('[new-account-sop] patch failed', err);
        // Roll back the optimistic write.
        if (snapshot) {
          const rollback = snapshot as SopPatch;
          setData((rows) =>
            rows.map((r) => (r.sop_id === sopId ? { ...r, ...rollback } : r)),
          );
        }
        throw err;
      }
    },
    [],
  );

  // #6 — delete via the delete_new_account_sop RPC. The DB enforces
  // owner-or-admin + a required reason and snapshots the row to
  // deletion_requests before the sanctioned hard delete. We optimistically
  // drop the row locally; the Realtime DELETE event also removes it for any
  // other open clients.
  const deleteRow = useCallback(async (sopId: string, reason: string) => {
    let removed: NewAccountSop | null = null;
    setData((rows) => {
      const hit = rows.find((r) => r.sop_id === sopId) ?? null;
      if (hit) removed = hit;
      return rows.filter((r) => r.sop_id !== sopId);
    });
    try {
      await deleteSopCard(sopId, reason);
    } catch (err) {
      console.error('[new-account-sop] delete failed', err);
      // Roll back the optimistic removal.
      if (removed) setData((rows) => [removed as NewAccountSop, ...rows]);
      throw err;
    }
  }, []);

  const clearRefusalNotice = useCallback(
    () => setRefusalNotice(null),
    [],
  );

  return useMemo(
    () => ({
      data,
      isLoading,
      error,
      lastUpdated,
      refetch,
      patchRow,
      deleteRow,
      refusalNotice,
      clearRefusalNotice,
    }),
    [
      data,
      isLoading,
      error,
      lastUpdated,
      refetch,
      patchRow,
      deleteRow,
      refusalNotice,
      clearRefusalNotice,
    ],
  );
}
